import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { MoneyDisplay } from '@/components/shared/money-display';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { MoneyInput } from '@/components/ui/money-input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

export interface FinancialPositionAdjustBalanceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
  itemName: string;
  currency: string;
  currentBalanceMinor: number;
  currentBalanceAsOfDate: string;
}

export const FinancialPositionAdjustBalanceDialog = ({
  open,
  onOpenChange,
  itemId,
  itemName,
  currency,
  currentBalanceMinor,
  currentBalanceAsOfDate,
}: FinancialPositionAdjustBalanceDialogProps) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [newBalanceMinor, setNewBalanceMinor] = useState(currentBalanceMinor);
  const [balanceAsOfDate, setBalanceAsOfDate] = useState(currentBalanceAsOfDate);
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [wasOpen, setWasOpen] = useState(open);
  const [syncedBalanceMinor, setSyncedBalanceMinor] = useState(currentBalanceMinor);
  const [syncedBalanceAsOfDate, setSyncedBalanceAsOfDate] = useState(currentBalanceAsOfDate);

  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setNewBalanceMinor(currentBalanceMinor);
      setBalanceAsOfDate(currentBalanceAsOfDate);
      setSyncedBalanceMinor(currentBalanceMinor);
      setSyncedBalanceAsOfDate(currentBalanceAsOfDate);
      setReason('');
      setPassword('');
    } else {
      setPassword('');
    }
  } else if (
    open &&
    (currentBalanceMinor !== syncedBalanceMinor || currentBalanceAsOfDate !== syncedBalanceAsOfDate)
  ) {
    setSyncedBalanceMinor(currentBalanceMinor);
    setSyncedBalanceAsOfDate(currentBalanceAsOfDate);
    setNewBalanceMinor(currentBalanceMinor);
    setBalanceAsOfDate(currentBalanceAsOfDate);
  }

  const save = useMutation({
    mutationFn: () =>
      api.post(`/financial-position/items/${itemId}/balance-adjustments`, {
        newBalanceMinor,
        balanceAsOfDate,
        reason: reason.trim(),
        password,
      }),
    onSuccess: () => {
      toast.success(t('financialPosition.adjustBalance.saved'));
      setPassword('');
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ['financial-position'] });
      void queryClient.invalidateQueries({ queryKey: ['reports', 'run', 'balance_sheet'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const reasonValid = reason.trim().length >= 3;
  const balanceChanged =
    newBalanceMinor !== currentBalanceMinor || balanceAsOfDate !== currentBalanceAsOfDate;
  const canSave = reasonValid && balanceChanged && password.length > 0 && !save.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent size="md" closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t('financialPosition.adjustBalance.title')}</DialogTitle>
          </DialogHeader>

          <DialogBody contentClassName="flex flex-col gap-4 overflow-visible">
            <p className="text-body-sm text-fg-secondary">
              {t('financialPosition.adjustBalance.description', { name: itemName })}
            </p>

            <div className="rounded-lg border border-border bg-surface-secondary px-4 py-3">
              <p className="text-caption text-fg-secondary">
                {t('financialPosition.adjustBalance.currentBalance')}
              </p>
              <MoneyDisplay
                amount={currentBalanceMinor}
                currency={currency}
                className="text-heading-sm font-semibold text-fg"
              />
            </div>

            <FormField
              label={t('financialPosition.adjustBalance.newBalance')}
              hint={t('financialPosition.adjustBalance.newBalanceHint')}
            >
              {(field) => (
                <MoneyInput
                  id={field.id}
                  value={newBalanceMinor}
                  currency={currency}
                  onChange={(value) => setNewBalanceMinor(value ?? currentBalanceMinor)}
                />
              )}
            </FormField>

            <FormField label={t('financialPosition.asOf')} required>
              {(field) => (
                <DatePicker
                  id={field.id}
                  popoverPortal="dialog"
                  value={balanceAsOfDate}
                  onChange={(value) =>
                    setBalanceAsOfDate(value ?? currentBalanceAsOfDate)
                  }
                />
              )}
            </FormField>

            <FormField label={t('financialPosition.adjustBalance.reason')} required>
              {(field) => (
                <Textarea
                  id={field.id}
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder={t('financialPosition.adjustBalance.reasonPlaceholder')}
                />
              )}
            </FormField>

            <FormField
              label={t('financialPosition.adjustBalance.password')}
              hint={t('financialPosition.adjustBalance.passwordHint')}
              required
            >
              {(field) => (
                <Input
                  id={field.id}
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  disabled={save.isPending}
                  aria-invalid={field.invalid}
                  aria-describedby={field['aria-describedby']}
                />
              )}
            </FormField>
          </DialogBody>

          <DialogFooter className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={save.isPending}
              onClick={() => onOpenChange(false)}
            >
              {tCommon('action.cancel')}
            </Button>
            <Button
              type="button"
              disabled={!canSave}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              {t('financialPosition.adjustBalance.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
    </Dialog>
  );
};
